'use client';

import type { CustomerReportDocument } from '@dte/otw-azure-functions/src/common/interfaces/containers/premises/CustomerReportDocument';
import { addSearchParamToUrl } from '@dte/otw-azure-functions/src/common/util/network/url/addSearchParamsToUrl';
import { validateOptionalPremiseId } from '@dte/otw-azure-functions/src/common/util/validation/validatePremiseId';
import { Grid, type MantineColor, Progress, useMantineTheme } from '@mantine/core';
import { secondsToMilliseconds } from 'date-fns/secondsToMilliseconds';
import { type PropsWithChildren, type ReactNode, useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import type { SWRConfiguration } from 'swr';
import { SuccessAlert } from '../../../../../components/Alerts/SuccessAlert';
import { Spinner } from '../../../../../components/Spinner/Spinner';
import { AuthenticatedJsonContent } from '../../content/jsonGet/AuthenticatedJsonContent';
import { joinUrlPaths } from '../../orders/(orderId)/reportedCalls/getPathSection';
import { PremiseLink } from '../../premises/(premiseId)/premiseLink/PremiseLink';
import { PremiseStatusDisplay } from '../../premises/(premiseId)/status/PremiseStatusDisplay';
import { useIsDraftSubmitted, useReportPremiseId } from '../_atoms/draftReport/draftReport';
import { SelfServicePromotionAdvisement } from '../questions/(premise)/review/summary';
import { HideFromNonPremiseReport } from '../questions/HideFromNonPremise';
import { useReportIdParam } from '../useReportIdParam';
import styles from './page.module.scss';

/**
 * Get the URL for fetching report data
 *
 * @param reportId
 * @param premiseId
 * @returns
 */
export function getCustomerReportURL(reportId: string | undefined, premiseId?: string): string | undefined {
	if (!reportId) {
		return undefined;
	}

	const paths = ['authenticated', 'data', 'customerReports', reportId];

	let url = joinUrlPaths(paths);
	if (!url) {
		return undefined;
	}

	const premiseIdValue = validateOptionalPremiseId(premiseId);
	if (premiseIdValue) {
		// add parameter
		url = addSearchParamToUrl(url, 'premiseId', premiseIdValue);
	}

	return url;
}

interface ContentAfterDelayProps extends PropsWithChildren {
	// Delay in milliseconds
	delay?: number;
	// Interval between updating the bar in milliseconds
	interval?: number;
	label?: ReactNode;
	color?: MantineColor;
}

export function ContentAfterDelay(props: ContentAfterDelayProps): ReactNode {
	const delay = props.delay ?? secondsToMilliseconds(15);
	const interval = props.interval ?? 50;
	const [remainingTime, setRemainingTime] = useState(delay);

	const color = props.color ?? 'blue.9';

	useEffect(() => {
		if (remainingTime <= 0) {
			return undefined;
		}

		const delayTimer = setTimeout(() => {
			setRemainingTime((prev) => prev - interval);
		}, interval);

		return () => {
			clearTimeout(delayTimer);
		};
	}, [remainingTime, interval]);

	if (remainingTime <= 0 && props.children) {
		return props.children;
	}

	const progression = (100 * (delay - remainingTime)) / delay;

	return (
		<Progress.Root transitionDuration={interval} className={styles['progressBar']} autoContrast={true}>
			<Progress.Section value={progression} color={color} animated={true}>
				<Progress.Label>{props.label}</Progress.Label>
			</Progress.Section>
		</Progress.Root>
	);
}

function WaitingForCallId(): ReactNode {
	const theme = useMantineTheme();

	return (
		<ContentAfterDelay
			delay={secondsToMilliseconds(15)}
			label={'waiting for report to be processed...'}
			color={theme.colors.green[9]}
		>
			will be available under <b>Outage History</b> once the report has been processed
		</ContentAfterDelay>
	);
}

interface CallIdLabelProps {
	callId: string | undefined;
}

function CallIdLabel({ callId }: CallIdLabelProps): ReactNode {
	if (callId) {
		return <b>{callId}</b>;
	}

	return <WaitingForCallId />;
}

function ConfirmationNumberDisplay(): ReactNode {
	const premiseId = useReportPremiseId();
	const reportId = useReportIdParam();
	const [callId, setCallId] = useState<string | undefined>();

	const updateCallId = useCallback((customerReport: CustomerReportDocument | undefined) => {
		// Only update if we have a call id
		const newCallId = customerReport?.callId;
		if (newCallId) {
			setCallId(newCallId);
		}
	}, []);

	let url: string | undefined;
	if (!callId) {
		url = getCustomerReportURL(reportId, premiseId);
	}

	const refreshConfig: SWRConfiguration = {
		// Refresh every 2 seconds until we have a call id
		focusThrottleInterval: secondsToMilliseconds(2),
		refreshInterval: secondsToMilliseconds(2),
		// Reduce dedupe interval
		dedupingInterval: secondsToMilliseconds(1),
	};

	return (
		<>
			<Grid>
				<Grid.Col span={{ base: 12, md: 3 }}>Confirmation Number</Grid.Col>
				<Grid.Col span={{ base: 12, md: 9 }}>
					<CallIdLabel callId={callId} />
				</Grid.Col>
			</Grid>
			<AuthenticatedJsonContent url={url} options={refreshConfig} setData={updateCallId} loadingContent={null} />
		</>
	);
}

function ConfirmationStatusDisplay(): ReactNode {
	const premiseId = useReportPremiseId();
	return <PremiseStatusDisplay premiseId={premiseId} />;
}

function ConfirmationResultDisplay(): ReactNode {
	const submitted = useIsDraftSubmitted();

	let title = 'Your report is being submitted';
	if (submitted) {
		title = 'Your report has been successfully submitted';
	}

	return (
		<SuccessAlert title={title}>
			<ConfirmationNumberDisplay />
		</SuccessAlert>
	);
}

function SubmissionSuccessfulContent(): ReactNode {
	const premiseId = useReportPremiseId();

	// Show a spinner while submitting
	const submitted = useIsDraftSubmitted();
	if (!submitted) {
		return <Spinner />;
	}

	return (
		<>
			<ConfirmationStatusDisplay />

			<br />

			{/* Link back to premise shouldn't appear for non-premise reports */}
			<HideFromNonPremiseReport>
				<PremiseLink premiseId={premiseId}>
					{/* TODO: this should be a link styled as a button, not a button */}
					<Button>Return to Premise</Button>
				</PremiseLink>
			</HideFromNonPremiseReport>
		</>
	);
}

export default function Page(): ReactNode {
	return (
		<div className={styles['main']}>
			<ConfirmationResultDisplay />
			<SelfServicePromotionAdvisement />

			<SubmissionSuccessfulContent />
		</div>
	);
}
